import { useState, useEffect, useRef } from "react";
import { useRoute } from "wouter";
import { useAuth } from '../hooks/use-auth';
import { useQuery } from '@tanstack/react-query';
import { authInterceptor } from '../lib/auth-interceptor';
import { generatePdfHtml, PdfTemplateConfig, DEFAULT_PDF_TEMPLATE_CONFIG } from '../components/pdf/unified-pdf-template';
import { getDisplayNotes } from '../lib/estimate-notes';

export default function EstimatePrintPage() {
  const [, params] = useRoute("/estimates/:id/print");
  const estimateId = params?.id ? parseInt(params.id) : 0;
  const { user } = useAuth();
  const [pdfTemplateConfig, setPdfTemplateConfig] = useState<PdfTemplateConfig>(DEFAULT_PDF_TEMPLATE_CONFIG);
  const hasPrintedRef = useRef(false);

  // Fetch estimate data (always refetch when opening print so client address is fresh)
  const { data: estimate, isLoading } = useQuery({
    queryKey: [`/api/protected/estimates`, estimateId],
    queryFn: async () => {
      const response = await fetch(`/api/protected/estimates?id=${estimateId}`, {
        credentials: 'include',
        headers: {
          ...authInterceptor.addAuthHeaders()
        }
      });
      if (!response.ok) throw new Error('Failed to fetch estimate');
      return response.json();
    },
    enabled: estimateId !== null && !isNaN(estimateId),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const clientId = estimate?.clientId ?? estimate?.client?.id;
  const hasClientAddress = estimate?.client && [
    estimate.client.address,
    estimate.client.city,
    estimate.client.state,
    (estimate.client as { zip?: string })?.zip,
  ].some(Boolean);

  // If estimate has a client but no address, fetch full client so we can show address
  const { data: fullClient } = useQuery({
    queryKey: ['/api/protected/clients', clientId],
    queryFn: async () => {
      const response = await fetch(`/api/protected/clients?id=${clientId}`, {
        credentials: 'include',
        headers: { ...authInterceptor.addAuthHeaders() },
      });
      if (!response.ok) throw new Error('Failed to fetch client');
      return response.json();
    },
    enabled: !!clientId && !!estimate && !hasClientAddress,
    staleTime: 0,
  });

  // Load PDF template configuration (same source as client-facing docs)
  useEffect(() => {
    const loadPdfConfig = async () => {
      try {
        const response = await fetch('/api/protected/contractor/settings', {
          credentials: 'include',
          headers: {
            ...authInterceptor.addAuthHeaders()
          }
        });

        if (response.ok) {
          const data = await response.json();
          setPdfTemplateConfig(data.pdfTemplate ? { ...DEFAULT_PDF_TEMPLATE_CONFIG, ...data.pdfTemplate } : DEFAULT_PDF_TEMPLATE_CONFIG);
        } else {
          setPdfTemplateConfig(DEFAULT_PDF_TEMPLATE_CONFIG);
        }
      } catch (error) {
        console.error('Error loading PDF template:', error);
        setPdfTemplateConfig(DEFAULT_PDF_TEMPLATE_CONFIG);
      }
    };

    loadPdfConfig();
  }, []);

  // Auto-print once when estimate is loaded (avoid reopening print dialog on re-renders)
  useEffect(() => {
    if (!isLoading && estimate && !hasPrintedRef.current) {
      hasPrintedRef.current = true;
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoading, estimate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Same template as client view (config from API or shared default)
  const config = pdfTemplateConfig;

  const clientForPdf = fullClient
    ? { ...estimate.client, address: fullClient.address, city: fullClient.city, state: fullClient.state, zip: fullClient.zip }
    : estimate.client;

  const contractorFromEstimate = estimate?.contractor as Record<string, unknown> | undefined;
  const contractor = {
    ...contractorFromEstimate,
    companyName: user?.companyName ?? contractorFromEstimate?.companyName ?? contractorFromEstimate?.company_name,
    email: user?.email ?? contractorFromEstimate?.email,
    phone: user?.phone ?? contractorFromEstimate?.phone ?? contractorFromEstimate?.contractor_phone,
    address: user?.address ?? contractorFromEstimate?.address ?? contractorFromEstimate?.contractor_address,
    city: user?.city ?? contractorFromEstimate?.city ?? contractorFromEstimate?.contractor_city,
    state: user?.state ?? contractorFromEstimate?.state ?? contractorFromEstimate?.contractor_state,
    zip: user?.zip ?? contractorFromEstimate?.zip ?? contractorFromEstimate?.contractor_zip,
    logoUrl: user?.logoUrl ?? contractorFromEstimate?.logoUrl ?? contractorFromEstimate?.logo_url
  };

  // Normalize client signature field (supports both camelCase and snake_case)
  const clientSignature =
    (estimate as any).clientSignature || (estimate as any).client_signature || null;

  const htmlContent = generatePdfHtml({
    data: {
      ...estimate,
      // Ensure both shapes are available to the template
      clientSignature,
      client_signature: clientSignature,
      notes: getDisplayNotes(estimate.notes),
      client: clientForPdf,
      contractor
    },
    config,
    type: 'estimate',
    // Consider the estimate "signed" whenever we have a signature and a signed/accepted status
    isSigned:
      !!clientSignature &&
      (estimate.status === 'accepted' ||
        (estimate as any).status === 'signed' ||
        (estimate as any).status === 'approved')
  });

  return (
    <div
      className="estimate-print-wrapper"
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
}